import groq from "groq";
import type { GetStaticPaths, GetStaticProps } from "next";
import { Debug } from "../../components/Debug";
import { SectionTitle } from "../../components/SectionTitle";
import { Finder } from "../../finder/Finder";
import { FILTERS } from "../../finder/settings";
import { usePreviewSubscription } from "../../helpers/sanity/sanity";
import { getClient } from "../../helpers/sanity/server";
import { linkUrlGroq } from "../../sanity/schemas/link";
import { SchemaType } from "../../sanity/types";
import { FooterProps, footerQuery } from "../../sections/Footer";
import { heroGroq } from "../../sections/Hero.sanity";
import { navQuery } from "../../sections/Nav";
import { richTextGroq } from "../../sections/RichText.sanity";
import { sectorInfoGroq } from "../../sections/SectorInfo.sanity";
import { storiesCarouselGroq } from "../../sections/StoriesCarousel.sanity";
import { textLinksGroq } from "../../sections/TextLinks.sanity";
import { ExpertiseTemplate } from "../../templates/ExpertiseTemplate";
import { FinderFilterType, NavType, PartnerSimpleType } from "../../types";
import { ColorShiftTrigger } from "../../wrappers/ColorShift";
import { Page } from "../../wrappers/Page";

const schemaType: SchemaType = "expertise";

/**
 * Sanity Query
 */

const pageQuery = groq` {
  "page": *[_type == "${schemaType}" && slug.current == $slug]{
    title, 
    "slug": slug.current,
    "hero": hero{
      ${heroGroq}
    },
    bodyTitle,
    "body": body[]{
      ${richTextGroq}
    },
    facts,
    "sectorInfo": sectorInfo{
      ${sectorInfoGroq}
    },
    "storiesCarousel": storiesCarousel{
      ${storiesCarouselGroq}
    },
    "textLinks": textLinks{
      ${textLinksGroq}
    },
    "related": {
      "title": relatedTitle,
      "items": cards[]{
        title,
        subject,
        image,
        "url": ${linkUrlGroq}
      }
    },
  }[0],
  "partners": *[_type == "partner"] {
    "slug": slug.current,
    "name": title,
    "avatar": avatar.asset->url,
    url,
  },
  ${footerQuery},
  ${navQuery},
}
`;

interface PageQueryType {
  page: {
    title?: string;
    slug?: string;
  };
  partners: PartnerSimpleType[];
  nav: NavType;
  footer: FooterProps | {};
}

/**
 * Page
 */

interface Props {
  data: {
    post: PageQueryType;
  };
  preview: boolean;
}

const SlugPage = ({
  data = { post: { page: {}, nav: [], partners: [], footer: {} } },
  preview,
}: Props) => {
  const { data: post } = usePreviewSubscription(pageQuery, {
    params: { slug: data.post?.page?.slug },
    initialData: data.post,
    enabled: preview && Boolean(data.post?.page?.slug),
  });

  if (!post?.page) {
    return <Page notFound={true} nav={post.nav} footer={post.footer} />;
  }

  const { title, slug } = post.page;

  /**
   * find the matching filter for the current slug page, e.g 'ict'
   * and set that option to true and also hidden
   */

  const FILTERS_FOR_EXPERTISE: FinderFilterType[] = JSON.parse(
    JSON.stringify(FILTERS)
  ).map((filter: FinderFilterType) => {
    if (filter.key === "vakgebied") {
      filter.amountActive = 1;
      filter.hidden = true;
      let filterValue = slug ? slug.replace(/-/g, " ") : "";
      const optionForExpertise = filter.options.filter(
        ({ value }) => value === filterValue
      )[0];
      if (optionForExpertise) {
        optionForExpertise.checked = true;
      }
    }
    return filter;
  });

  return (
    <>
      <Page
        title={title}
        notFound={!slug}
        nav={post.nav}
        preview={preview}
        footer={post.footer}
      >
        <ExpertiseTemplate page={post.page} />
        <ColorShiftTrigger color="white">
          <div
            style={{
              margin: "var(--spacing-lg) var(--outer-space)  var(--spacing-xs)",
            }}
          >
            <SectionTitle deco="top-center-scribble-yellow" align="center">
              Opleidingen en cursussen
            </SectionTitle>
          </div>
          <Finder
            partners={post.partners}
            initialFilters={FILTERS_FOR_EXPERTISE}
            pageSlug={slug}
          />
        </ColorShiftTrigger>
      </Page>
      {preview && <Debug theVar={post.page} />}
    </>
  );
};

export default SlugPage;

/**
 * Static props
 */

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
}) => {
  const post: PageQueryType = await getClient(preview).fetch(pageQuery, {
    slug: params?.slug,
  });

  if (!post) return { props: { notFound: true } };

  return {
    props: {
      preview,
      data: { post },
    },
    revalidate: 60,
  };
};

/**
 * Static paths
 */

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: string[] = await await getClient(false).fetch(
    `*[_type == "${schemaType}"] { "slug": slug.current }.slug`
  );

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: true,
  };
};
